import { useCallback, useContext, useMemo } from 'react';
import type { BarChart, MultipleBarChart, MultipleLineChart, PieChart, LineChart } from '@trinity/chat';
import { NivoBarChart } from '@trinity/chat-components/components/Widgets/Charts/Bar.js';
import { NivoDoughnutChart } from '@trinity/chat-components/components/Widgets/Charts/Doughnut.js';
import { NivoLineChart } from '@trinity/chat-components/components/Widgets/Charts/Line.js';
import { NivoPieChart } from '@trinity/chat-components/components/Widgets/Charts/Pie.js';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Widgets/Charts/StackedBar.js';
import { useOptionsColors } from '@trinity/chat-components/containers/AppTheming/useOptionsColors';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';
import { NivoMultipleBarChart } from '../Widgets/Charts/MultipleBar';
import { ChartInfoContext, ChartTypeContext, ChartGroupByContext, ChartThemeIdContext } from './context';
import { getGroupedBarChartData, getGroupedPieChartData } from './Message.Response.Chart.utils';

type LegendItem = {
  key: string;
  color: string;
};

function buildLegendItems(chartType: string, chartData: unknown, groupBy: unknown, colors: string[]): LegendItem[] {
  switch (chartType) {
    case 'stacked-bar':
    case 'bar': {
      const data = getGroupedBarChartData((chartData as BarChart).data, groupBy as number);
      return data.map((d, i) => ({ key: d.x, color: colors[i] }));
    }
    case 'pie': {
      const data = getGroupedPieChartData((chartData as PieChart).data, groupBy as number);
      return data.map((d, i) => ({ key: d.label, color: colors[i] }));
    }
    case 'multiple-stacked-bar':
    case 'multiple-clustered-bar': {
      const multipleChart = chartData as MultipleBarChart;
      const keys = new Set<string>();
      for (const group of multipleChart.data) {
        for (const item of group.data) {
          keys.add(item.x);
        }
      }
      return [...keys].map((key, i) => ({ key, color: colors[i] }));
    }
    case 'multiple-line': {
      const lineChart = chartData as MultipleLineChart;
      const keys = new Set<string>();
      for (const line of lineChart.data) {
        keys.add(line.id);
      }
      return [...keys].map((key, i) => ({ key, color: colors[i] }));
    }
    case 'doughnut': {
      const pieChart = chartData as PieChart;
      const data = getGroupedPieChartData(pieChart.data, groupBy as number);
      return data.map((d, i) => ({ key: d.label, color: colors[i] }));
    }
    default:
      return [];
  }
}

function useChartColors(): string[] {
  const themeIdCtx = useContext(ChartThemeIdContext);
  const selectedThemeId = themeIdCtx?.[0] ?? null;
  const { data } = useContext(BrandThemingContext);
  const defaultColors = useOptionsColors();

  return useMemo(() => {
    if (!selectedThemeId || !data?.categories) {
      return defaultColors;
    }
    for (const categoryGroup of data.categories) {
      const theme = categoryGroup.items.find(item => item.id === selectedThemeId);
      if (theme) return theme.colors;
    }
    return defaultColors;
  }, [selectedThemeId, data?.categories, defaultColors]);
}

export const useResponseChartLegend = () => {
  const [chart] = useContext(ChartInfoContext);
  const [chartType] = useContext(ChartTypeContext);
  const [groupBy] = useContext(ChartGroupByContext);
  const colors = useChartColors();

  return useMemo(() => {
    const chartData = chart?.chart?.chart;
    if (!chartData) return [];
    return buildLegendItems(chartType, chartData, groupBy, colors);
  }, [chart, chartType, groupBy, colors]);
};

type RenderResponseChart = {
  legend: LegendItem[];
  unitInfo?: {
    unit: string;
    isPrefix: boolean;
  };
};

function renderBarOrStackedBar(chartData: BarChart, groupBy: unknown, colors: string[], unitInfo: RenderResponseChart['unitInfo'], stacked: boolean) {
  const data = getGroupedBarChartData(chartData.data, groupBy as number);
  const chartColors = data.map((_, i) => colors[i]);
  const chart = { ...chartData, data, indexBy: chartData.xAxisTitle };
  if (stacked) {
    return <NivoStackedBarChart chart={chart} colors={chartColors} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
  }
  return <NivoBarChart chart={chart} colors={chartColors} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
}

function renderPieChart(chartData: unknown, groupBy: unknown, colors: string[], unitInfo: RenderResponseChart['unitInfo']) {
  const data = getGroupedPieChartData((chartData as PieChart).data, groupBy as number);
  const chartColors = data.map((_, i) => colors[i]);
  return <NivoPieChart chart={{ ...(chartData as PieChart), data }} colors={chartColors} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
}

function renderDoughnutChart(chartData: unknown, groupBy: unknown, colors: string[], legend: LegendItem[]) {
  const pieChart = chartData as PieChart;
  const data = getGroupedPieChartData(pieChart.data, groupBy as number);
  for (let i = 0; i < data.length; i++) {
    legend.push({ key: data[i].label, color: colors[i] });
  }
  return <NivoDoughnutChart chart={{ ...pieChart, data }} colors={colors} />;
}

function renderChartByType(
  chartType: string,
  chartData: unknown,
  groupBy: unknown,
  colors: string[],
  legend: LegendItem[],
  unitInfo: RenderResponseChart['unitInfo'],
) {
  switch (chartType) {
    case 'stacked-bar':
      return renderBarOrStackedBar(chartData as BarChart, groupBy, colors, unitInfo, true);
    case 'bar':
      return renderBarOrStackedBar(chartData as BarChart, groupBy, colors, unitInfo, false);
    case 'pie':
      return renderPieChart(chartData, groupBy, colors, unitInfo);
    case 'line':
      return <NivoLineChart chart={chartData as LineChart} colors={colors} />;
    case 'doughnut':
      return renderDoughnutChart(chartData, groupBy, colors, legend);
    case 'multiple-stacked-bar':
      return <NivoMultipleBarChart chart={chartData as MultipleBarChart} colors={colors} isStacked={true} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
    case 'multiple-clustered-bar':
      return <NivoMultipleBarChart chart={chartData as MultipleBarChart} colors={colors} isStacked={false} unit={unitInfo?.unit} isPrefix={unitInfo?.isPrefix} />;
    case 'multiple-line':
      return <NivoLineChart chart={chartData as MultipleLineChart} colors={colors} />;
    default:
      return null;
  }
}

export const useRenderResponseChart = (props: RenderResponseChart) => {
  const { legend, unitInfo } = props;
  const [chart] = useContext(ChartInfoContext);
  const [groupBy] = useContext(ChartGroupByContext);
  const colors = useChartColors();

  const renderChart = useCallback((chartType: string) => {
    const chartData = chart.chart.chart;
    return renderChartByType(chartType, chartData, groupBy, colors, legend, unitInfo);
  }, [chart, groupBy, colors, legend, unitInfo?.unit, unitInfo?.isPrefix]);

  return renderChart;
};
